import { PathwayOutletContext, PathwayIntervention } from '@/types';
import {
  Link,
  useNavigate,
  Navigate,
  useOutletContext,
} from 'react-router-dom';
import { useCallback, useState } from 'react';
import { useInterventionKey } from '@/hooks/use-intervention-key';
import { useTheme } from 'styled-components';
import {
  ConfirmationDialog,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuItem,
  DropdownMenuList,
  Button,
  UtilityButton,
  Separator,
  Container,
  Frame,
  Content,
  Header,
  Toolbar,
  ToolbarGroup,
  ToolbarItem,
  H2,
} from '@kinesis/bungle';
import { InterventionEdit } from '@/components/intervention-edit';
import { pathwayPath } from '@/links';

// The open-intervention editor on the new pathway — the legacy
// scenario-assumptions structure against the pathway context. Targeting
// offers ride the context's contextual sub-sectors. (No copy-to-scenario:
// cross-model copies are not a thing; the legacy pages keep theirs.)
const PathwayAssumptions = () => {
  const { pathway, removeIntervention, updateIntervention, subSectors } =
    useOutletContext<PathwayOutletContext>();
  const theme = useTheme() as any;
  const navigate = useNavigate();
  const interventionKey = useInterventionKey();
  const intervention = pathway.interventions.find(
    (i) => i.id == interventionKey,
  );
  const detailPath = pathwayPath(pathway.inventory, pathway.id);

  const onUpdate = useCallback(
    (f: (x: PathwayIntervention) => PathwayIntervention) => {
      if (interventionKey) {
        updateIntervention(interventionKey, f);
      }
    },
    [updateIntervention, interventionKey],
  );

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const openConfirmDelete = useCallback(
    () => setShowConfirmDelete(true),
    [setShowConfirmDelete],
  );
  const closeConfirmDelete = useCallback(
    () => setShowConfirmDelete(false),
    [setShowConfirmDelete],
  );
  const onDeleteIntervention = useCallback(() => {
    if (interventionKey) {
      removeIntervention(interventionKey);
    }
    navigate(detailPath);
  }, [navigate, removeIntervention, detailPath, interventionKey]);

  if (!intervention) {
    return <Navigate to={detailPath} />;
  }
  return (
    <>
      <div
        style={{
          paddingLeft: 80,
          background: theme.color.monochrome2,
          height: '100%',
        }}
      >
        <Content padding='none' background='white' height='100%' borderLeft>
          <Container>
            <Header border>
              <Toolbar>
                <ToolbarGroup>
                  <ToolbarItem>
                    <UtilityButton
                      as={Link}
                      to={detailPath}
                      icon='arrow-right'
                      display='block'
                    />
                  </ToolbarItem>
                  <Separator />
                  <ToolbarItem ellipsify>
                    <H2>{intervention.name}</H2>
                  </ToolbarItem>
                </ToolbarGroup>
                <ToolbarGroup>
                  <ToolbarItem>
                    <DropdownMenu justify='end'>
                      <DropdownMenuButton
                        as={Button}
                        variant='secondary'
                        dropdown
                        inline
                        magnitude='medium'
                      >
                        Manage intervention
                      </DropdownMenuButton>
                      <DropdownMenuList>
                        <DropdownMenuItem
                          onSelect={openConfirmDelete}
                          variant='danger'
                        >
                          Delete intervention
                        </DropdownMenuItem>
                      </DropdownMenuList>
                    </DropdownMenu>
                  </ToolbarItem>
                </ToolbarGroup>
              </Toolbar>
            </Header>
            <Frame sizing='fill-container'>
              <InterventionEdit
                intervention={intervention}
                onUpdate={onUpdate}
                subSectors={subSectors}
              />
            </Frame>
          </Container>
        </Content>
      </div>
      {showConfirmDelete && (
        <ConfirmationDialog
          cancelText="Don't delete"
          confirmText='Delete intervention'
          onCancel={closeConfirmDelete}
          onConfirm={onDeleteIntervention}
          title={`Delete intervention "${intervention.name}"?`}
          variant='danger'
        >
          This intervention and its assumptions will be removed from your
          pathway. You will have a chance to undo this before you save your
          pathway.
        </ConfirmationDialog>
      )}
    </>
  );
};

export { PathwayAssumptions };
